import React, { useState } from "react";
import { Link } from "react-router-dom";
import MusicShell from "../fanHome/MusicShell";
import { isSignedIn } from "../fanHome/fanHomeApi";
import { soundApi } from "../soundDetail/soundApi";
import { ACCENT, Field, PrimaryButton, SecondaryButton, inputClass, ringStyle } from "../artistStudio/shared";
import { REPORT_REASONS, claimErrorText, fieldErrors, rightsClaimApi } from "./rightsClaimApi";
import { soundIdFrom } from "./RightsFormPage";

// /rights/report-audio (mobile design, screen 40): report a problem with a sound (explicit content not marked, misleading,
// offensive, spam, other) from the Rights & legal hub. Copyright and ownership problems use the notice and claim pages.
// The person says which sound (its link or number), then picks a reason. One open report per person and sound.
export default function ReportAudioPage() {
  const [text, setText] = useState("");
  const [finding, setFinding] = useState(false);
  const [findError, setFindError] = useState("");
  const [sound, setSound] = useState(null);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const find = async (event) => {
    event.preventDefault();
    const id = soundIdFrom(text);
    if (!id) { setFindError("Paste the link of the sound (it ends in /sound/ and a number)."); return; }
    setFinding(true);
    setFindError("");
    try {
      const detail = await soundApi.detail(id);
      setSound({ id, title: detail?.title || `Sound #${id}` });
    } catch (e) {
      setFindError("We couldn't find that sound. Check the link and try again.");
    } finally {
      setFinding(false);
    }
  };

  const submit = async () => {
    const local = {};
    if (!reason) local.reason = "Choose a reason for the report.";
    if (reason === "other" && details.trim().length < 10) local.details = "Tell us what is wrong (at least 10 characters).";
    setErrors(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const body = { track_id: Number(sound.id), reason };
      if (details.trim()) body.details = details.trim();
      await rightsClaimApi.reportSound(body);
      setSent(true);
    } catch (e) {
      const fields = fieldErrors(e);
      if (Object.keys(fields).length) setErrors(fields);
      else setErrors({ form: claimErrorText(e, "We couldn't send your report. Please try again.") });
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setSound(null); setText(""); setReason(""); setDetails(""); setErrors({}); setSent(false); };

  return (
    <MusicShell maxWidth="max-w-2xl">
      <Link to="/rights" className="text-sm font-semibold hover:underline" style={{ color: ACCENT }}>← Rights &amp; legal</Link>
      <h1 className="mt-3 text-2xl md:text-3xl font-bold text-gray-900">Report audio</h1>
      {!isSignedIn() ? (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="report-login">
          <p className="text-gray-700">Sign in to report a sound. We need to know who is sending it.</p>
          <Link to="/login" className="mt-4 inline-block rounded-full px-5 py-2.5 font-semibold text-white" style={{ backgroundColor: ACCENT }}>Log in</Link>
        </div>
      ) : sent ? (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 text-center" data-testid="report-sent">
          <h2 className="text-xl font-bold text-gray-900">Thanks, we received your report</h2>
          <p className="mt-2 text-sm text-gray-600">Our team will review “{sound.title}”. Nothing is removed automatically.</p>
          <div className="mt-5 flex flex-col gap-2">
            <SecondaryButton onClick={reset}>Report another sound</SecondaryButton>
            <Link to="/rights" className="block rounded-full py-3 text-center font-semibold text-white" style={{ backgroundColor: ACCENT }}>Back to Rights &amp; legal</Link>
          </div>
        </div>
      ) : !sound ? (
        <form onSubmit={find} className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="report-find">
          <p className="mb-4 text-sm text-gray-600">Use this for explicit content that is not marked, misleading or offensive sounds, or spam. For a copyright problem, use a takedown notice or an ownership claim.</p>
          <Field label="Link to the sound" required error={findError}>
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="https://…/sound/123" className={inputClass(!!findError)} style={ringStyle} aria-label="Sound link" />
          </Field>
          <PrimaryButton type="submit" disabled={finding || !text.trim()} className="mt-4 w-full !py-3">{finding ? "Looking…" : "Continue"}</PrimaryButton>
        </form>
      ) : (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="report-form">
          <p className="text-sm text-gray-600">Reporting: <span className="font-semibold text-gray-900">{sound.title}</span> <button type="button" onClick={() => setSound(null)} className="ml-2 underline" style={{ color: ACCENT }}>Change</button></p>
          <div className="mt-4">
            <Field label="What is wrong?" required error={errors.reason}>
              <div className="space-y-2">
                {REPORT_REASONS.map((r) => (
                  <label key={r.value} className="flex items-start gap-2 text-sm text-gray-800">
                    <input type="radio" name="report_reason" checked={reason === r.value} onChange={() => setReason(r.value)} className="mt-1" />
                    <span><span className="font-semibold">{r.label}</span> - {r.hint}</span>
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <div className="mt-4">
            <Field label={reason === "other" ? "Details" : "Details (optional)"} required={reason === "other"} hint={reason === "other" ? "At least 10 characters." : undefined} error={errors.details}>
              <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} maxLength={1000} className={inputClass(!!errors.details)} style={ringStyle} />
            </Field>
          </div>
          {errors.form && <p role="alert" className="mt-3 text-sm text-red-600">{errors.form}</p>}
          <PrimaryButton onClick={submit} disabled={busy} className="mt-5 w-full !py-3" data-testid="report-submit">{busy ? "Sending…" : "Submit report"}</PrimaryButton>
        </div>
      )}
    </MusicShell>
  );
}
